import RoiCalculator from '../components/sections/RoiCalculator';
import '../styles/page-resource-optimizer.css';
import Seo from '../components/Seo';

/* Resource Optimizer, rebuilt in the landing's design.

   Every number, name and claim is carried over verbatim from the host module
   (`src/modules/resource-optimizer`) — the case study, the metrics, the ROI
   maths. Only the presentation changed, so the page stops switching to the old
   dark header halfway through a visit.

   If the host module's figures change, change them here too. */

const STEPS = [
  {
    n: '01',
    t: 'We study how you work',
    d: 'Understand how your business operates day-to-day — across teams, tools and processes — without disrupting your work.',
  },
  {
    n: '02',
    t: 'We find the leaks',
    d: 'Identify where time and money are being lost, and clearly see which processes can be improved or automated.',
  },
  {
    n: '03',
    t: 'We build the fix',
    d: 'We build and deploy tailored AI solutions inside your systems — improving efficiency while keeping full control in your hands.',
  },
];

const TIMELINE = [
  { t: 'Discovery & install', d: 'Deployed monitoring agents across 6 departments. Zero disruption to existing workflows.' },
  { t: 'Data collection', d: 'Our software silently tracked 2,847 unique workflow patterns across the entire organisation.' },
  { t: 'Analysis report', d: 'Delivered a 23-page analysis. Identified 18 repetitive processes consuming 127 hours/week across teams.' },
  { t: 'Results', d: 'Operations team reclaimed 100+ hours/week. CFO reported ₹10L/month in savings within the first quarter.' },
];

const METRICS = [
  { label: 'Hours saved / week', before: '127h manual', after: '11h manual', pct: '91%' },
  { label: 'Monthly cost reduction', before: '₹22L/mo', after: '₹4L/mo', pct: '82%' },
  { label: 'Processes automated', before: '0 of 18', after: '16 of 18', pct: '89%' },
  { label: 'Team redeployed to strategy', before: '12 on busywork', after: '12 on growth', pct: '100%' },
];

const SYSTEMS = [
  { name: 'Invoice Tracker', result: 'Reduced overdue invoices by 94%. Auto-sends reminders at 7, 14 and 30 days.' },
  { name: 'Report Generator', result: 'Weekly ops reports generated in 12 seconds instead of 6 hours.' },
  { name: 'CRM Auto-Sync', result: 'Eliminated 15h/week of manual data entry between 3 systems.' },
  { name: 'Email Follow-up Bot', result: 'Sends personalised follow-ups to 200+ clients. Response rate up 34%.' },
  { name: 'Meeting Summarizer', result: 'Extracts action items from every meeting. Assigns tasks automatically.' },
  { name: 'Inventory Alerts', result: 'Predicts stockouts two weeks ahead. Zero emergency orders since deploy.' },
];

const TRUST = [
  { v: '14', l: 'Organisations' },
  { v: '₹12Cr+', l: 'Revenue saved' },
  { v: '100+', l: 'Workflows built' },
];

const Check = () => (
  <svg aria-hidden="true" viewBox="0 0 16 16" className="chk">
    <path fill="none" stroke="#007A6E" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" d="M2 8.5 6 12.5 14 3.5" />
  </svg>
);

export default function ResourceOptimizerPage() {
  return (
    <>
      <Seo
        title="Resource Optimizer — AI Transformation | TeamGrid™"
        description="We study your organisation, map every repeatable task, and build the exact software to eliminate it. Start with a free 30-minute audit — no cost, no commitment."
      />

      <header className="ro-hero">
        <div className="container">
          <span className="hero-badge"><img className="mark" src="/assets/mark-teamgrid.svg" alt="" />AI Transformation Platform <span className="arr">›</span></span>
          <h1>Your company isn&apos;t slow.<br /><span className="dim">It&apos;s just not automated yet.</span></h1>
          <p className="ro-sub">
            We study how your organisation actually works, map every repeatable task, and build the
            exact software that removes it. Not advice — working systems, inside your stack.
          </p>
          <div className="ro-cta">
            <a className="btn-primary" href="/resource-optimizer/book">Book your free audit</a>
            <a className="btn-ghost" href="#calculator">Calculate your waste</a>
          </div>
          <p className="ro-note">Free 30-minute audit · No commitment · Just numbers</p>

          <div className="ro-trust">
            {TRUST.map((t) => (
              <div key={t.l}><span className="v">{t.v}</span><span className="l">{t.l}</span></div>
            ))}
          </div>
        </div>
      </header>

      <section className="sect">
        <div className="container">
          <span className="fl" style={{ color: '#007A6E' }}>HOW IT WORKS</span>
          <h2 className="sect-h2" style={{ fontSize: '38px', lineHeight: '46px' }}>Three steps.<br /><span className="dim">No pitch deck.</span></h2>
          <div className="ro-steps">
            {STEPS.map((s) => (
              <div className="ro-step" key={s.n}>
                <span className="ro-n">{s.n}</span>
                <h3>{s.t}</h3>
                <p>{s.d}</p>
              </div>
            ))}
          </div>
          <div className="ro-bar">
            <div>
              <strong>Want us to do this for your organisation?</strong>
              <span>Step 1 is a free 30-minute audit. We map 3–5 areas where automation pays for itself.</span>
            </div>
            <a className="btn-primary" href="/resource-optimizer/book">Start with a free audit</a>
          </div>
        </div>
      </section>

      <section className="sect ro-proof">
        <div className="container">
          <span className="fl" style={{ color: '#F2673A' }}>REAL RESULTS</span>
          <h2 className="sect-h2" style={{ fontSize: '38px', lineHeight: '46px' }}>A 300-person<br /><span className="dim">manufacturing firm</span></h2>
          <p className="sect-sub">Manufacturing &amp; distribution · Gujarat, India</p>

          <div className="ro-problem">
            <span className="fl" style={{ color: '#b43a18' }}>THE PROBLEM</span>
            <p>
              Senior operations team spending <b>60% of their time</b> on manual reporting, invoice
              tracking and data entry instead of strategic work.
            </p>
          </div>

          <ol className="ro-time">
            {TIMELINE.map((s, i) => (
              <li key={s.t}><span className="ro-tn">{i + 1}</span><div><h4>{s.t}</h4><p>{s.d}</p></div></li>
            ))}
          </ol>

          <div className="ro-metrics">
            {METRICS.map((m) => (
              <div className="ro-metric" key={m.label}>
                <span className="ro-mlab">{m.label}</span>
                <span className="ro-mpct">{m.pct}</span>
                <span className="ro-mba"><i>{m.before}</i><em>→</em><b>{m.after}</b></span>
              </div>
            ))}
          </div>

          <h3 className="ro-sysh">What we built for them</h3>
          <div className="ro-systems">
            {SYSTEMS.map((s) => (
              <div className="ro-sys" key={s.name}>
                <Check />
                <div><b>{s.name}</b><p>{s.result}</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <RoiCalculator />

      <section className="sect ro-close">
        <div className="container">
          <span className="fl" style={{ color: '#007A6E' }}>LET&apos;S BEGIN</span>
          <h2 className="sect-h2" style={{ fontSize: '42px', lineHeight: '50px' }}>No pitch deck.<br /><span className="dim">No sales script. Just numbers.</span></h2>
          <p className="sect-sub">
            Thirty minutes. We map the leaks, quantify them, and show you exactly which systems close
            each one. You keep the findings either way.
          </p>
          <a className="btn-primary" href="/resource-optimizer/book" style={{ marginTop: '26px' }}>Book your free audit</a>
        </div>
      </section>
    </>
  );
}
